import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { updateProducts } from "./apis";
import { BookingModalProps } from "./types";
import { OrderNotConfirmedError } from "service/moneeflo/legacy-forex-order-api";
import DeskDialog, { inputClass } from "./components/DeskDialog";

const reasonOptions = ["Stock unavailable", "No delivery person", "Others"];

/** A line starts from what core already has: accepted, rejected, or still waiting (empty). */
type Decision = { status: "Accept" | "Reject" | ""; reason: string };

/**
 * Accepting or rejecting each currency line of an order. A rejected line needs a reason, checked
 * here before anything is sent. When the lines save but the order cannot be confirmed, the
 * dialog closes with one short message and the blockers panel on the page says why.
 */
const ConfirmBookingModal = ({ onClose, onSubmit, initialData }: BookingModalProps) => {
  const queryClient = useQueryClient();
  const [decisions, setDecisions] = useState<Record<string, Decision>>(() =>
    Object.fromEntries(
      initialData.flatMap((customer) =>
        customer.product_detail.map((p) => {
          const current = (p as { status?: string; reason_for_reject?: string }).status;
          const status: Decision["status"] = current === "Accept" || current === "Reject" ? current : "";
          return [String(p.id), { status, reason: status === "Reject" ? (p as { reason_for_reject?: string }).reason_for_reject ?? "" : "" } as Decision];
        }),
      ),
    ),
  );
  const [showMissing, setShowMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (id: string, patch: Partial<Decision>) =>
    setDecisions((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const undecided = Object.values(decisions).filter((d) => d.status === "").length;
  const missingReason = Object.entries(decisions).filter(([, d]) => d.status === "Reject" && d.reason.trim().length < 3);

  const mutation = useMutation({
    mutationFn: updateProducts,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orderDetails"] });
      onSubmit?.(data);
      onClose();
      toast.success("Lines saved");
    },
    onError: async (e: unknown) => {
      // Some lines may have been saved before the order was refused, so the page is refreshed either way.
      const refreshed = queryClient.invalidateQueries({ queryKey: ["orderDetails"] });
      if (e instanceof OrderNotConfirmedError) {
        // One short line. The blockers panel carries what is missing; core's own sentence is added
        // only when the panel does not already say it.
        onClose();
        await refreshed.catch(() => undefined);
        await new Promise((r) => setTimeout(r, 300));
        const panel = (document.querySelector('[data-testid="forex-findings"]')?.textContent ?? "").replace(/\s+/g, " ").toLowerCase();
        // Core joins its blockers with semicolons; only the ones the panel does not show are added.
        const missing = e.reason
          .split(/;/)
          .map((part) => part.replace(/[\s.]+$/, "").trim())
          .filter((part) => part !== "" && !panel.includes(part.toLowerCase()));
        toast.message(e.message, missing.length ? { description: `${missing.join(". ")}.` } : undefined);
        return;
      }
      setError(e instanceof Error ? e.message : "The decisions could not be saved.");
    },
  });

  const submit = () => {
    setError(null);
    if (missingReason.length > 0 || undecided > 0) {
      setShowMissing(true);
      return;
    }
    mutation.mutate({
      order_id: 0,
      status: Object.entries(decisions).map(([id, d]) => ({
        id,
        status: d.status as "Accept" | "Reject",
        reason_for_reject: d.status === "Reject" ? d.reason.trim() : "",
      })),
    });
  };

  return (
    <DeskDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Confirm booking"
      description="Accept or reject each line. A rejected line needs a reason."
      busy={mutation.isLoading}
      submitLabel="Submit"
      closeLabel="Cancel"
      error={error}
      onSubmit={submit}
    >
      {initialData.map((customer) => (
        <section key={customer.id} aria-label={customer.name} className="flex flex-col gap-4">
          {initialData.length > 1 && <h3 className="text-sm font-semibold text-[#333333]">{customer.name}</h3>}
          {customer.product_detail.map((product) => {
            const id = String(product.id);
            const d = decisions[id];
            const needsReason = d?.status === "Reject";
            const missing = showMissing && needsReason && d.reason.trim().length < 3;
            return (
              <fieldset key={id} className="flex flex-col gap-2 rounded-lg border border-[#E5E5E5] p-3">
                <legend className="px-1 text-sm font-medium text-[#333333]">
                  {product.name} · {product.currency} {product.quantity}
                </legend>
                <div className="flex gap-4 text-sm">
                  {(["Accept", "Reject"] as const).map((choice) => (
                    <label key={choice} className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name={`decision-${id}`}
                        checked={d?.status === choice}
                        onChange={() => set(id, { status: choice, reason: choice === "Accept" ? "" : d?.reason ?? "" })}
                        className="h-4 w-4 cursor-pointer"
                      />
                      {choice}
                    </label>
                  ))}
                </div>
                {showMissing && d?.status === "" && (
                  <p role="alert" className="text-sm text-[#B00020]">
                    Accept or reject this line.
                  </p>
                )}
                {needsReason && (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap gap-2" role="group" aria-label={`Reason for rejecting ${product.name}`}>
                      {reasonOptions
                        .filter((tag) => tag !== "Others")
                        .map((tag) => (
                          <button
                            key={tag}
                            type="button"
                            aria-pressed={d.reason === tag}
                            onClick={() => set(id, { reason: tag })}
                            className={`cursor-pointer rounded-full border px-3 py-1.5 text-sm ${
                              d.reason === tag ? "border-[#165FE3] text-[#165FE3]" : "border-gray-300 text-[#444444] hover:bg-gray-50"
                            }`}
                          >
                            {tag}
                          </button>
                        ))}
                    </div>
                    <label htmlFor={`reason-${id}`} className="text-sm font-medium text-[#333333]">
                      Reason for rejecting
                    </label>
                    <textarea
                      id={`reason-${id}`}
                      rows={2}
                      value={d.reason}
                      onChange={(e) => set(id, { reason: e.target.value })}
                      aria-invalid={missing}
                      className={`${inputClass} resize-none ${missing ? "border-[#B00020]" : ""}`}
                    />
                    {missing && (
                      <p role="alert" className="text-sm text-[#B00020]">
                        Give a reason for rejecting this line.
                      </p>
                    )}
                  </div>
                )}
              </fieldset>
            );
          })}
        </section>
      ))}
    </DeskDialog>
  );
};

export default ConfirmBookingModal;
